import React from 'react';
import PropTypes from 'prop-types';
import Select, { components } from 'react-select';
import debounce from 'lodash/debounce';
import common from 'constants/common';
import DropdownClosedIcon from 'components/atoms/icons/dropdownClosedIcon';
import DropdownOpenIcon from 'components/atoms/icons/dropdownOpenIcon';

const SEARCH_DEBOUNCE_MS = 500;

const customStyles = {
  option: (styles, { isDisabled }) => ({
    ...styles,
    backgroundColor: isDisabled ? '#eee !important' : styles.backgroundColor,
    color: isDisabled ? '#999 !important' : styles.color,
    borderRadius: isDisabled ? '0 !important' : styles.borderRadius,
  }),
  menu: (styles) => ({
    ...styles,
    border: `1px solid var(--stroke)`,
    borderRadius: `2px`,
  }),
};

const AsyncSelectBox = ({
  defaultValue,
  valueChanged,
  loadOptions,
  isMulti,
  isClearable,
  disabled,
  dataCy,
}) => {
  const selectTheme = (theme) => ({
    ...theme,
    borderRadius: 12,
    colors: {
      ...theme.colors,
      ...common.primaryColors,
    },
  });

  const dropdownIndicatorIcon = (menuIsOpen) =>
    menuIsOpen ? <DropdownOpenIcon /> : <DropdownClosedIcon />;

  const DropdownIndicator = (props) => {
    // eslint-disable-next-line react/prop-types
    const { selectProps } = props;
    // eslint-disable-next-line react/prop-types
    const { menuIsOpen } = selectProps;
    return (
      <components.DropdownIndicator {...props}>
        {dropdownIndicatorIcon(menuIsOpen)}
      </components.DropdownIndicator>
    );
  };

  // When the form holds full options (multi select) the box is controlled by
  // them. When it holds a bare guid (single select) the label cannot be looked
  // up, because options are fetched on demand — so track the picked option here.
  const [picked, setPicked] = React.useState(null);
  const selected = defaultValue !== undefined ? defaultValue : picked;

  const onSelectionChange = (option) => {
    setPicked(option ?? null);
    valueChanged(option);
  };

  const [options, setOptions] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const search = React.useRef('');
  const page = React.useRef(1);
  const hasMore = React.useRef(true);
  // Guards against a slow earlier request overwriting a newer one.
  const requestId = React.useRef(0);

  const fetchPage = async (nextPage) => {
    const currentRequest = requestId.current + 1;
    requestId.current = currentRequest;

    setIsLoading(true);
    try {
      const result = await loadOptions(search.current, nextPage);

      if (requestId.current !== currentRequest) return;

      page.current = nextPage;
      hasMore.current = result.hasMore;
      setOptions((prev) =>
        nextPage === 1 ? result.options : [...prev, ...result.options],
      );
    } catch (error) {
      if (requestId.current !== currentRequest) return;
      hasMore.current = false;
    } finally {
      if (requestId.current === currentRequest) setIsLoading(false);
    }
  };

  const debouncedSearch = React.useMemo(
    () => debounce(() => fetchPage(1), SEARCH_DEBOUNCE_MS),
    [loadOptions],
  );

  const onInputChange = (value, { action }) => {
    // Ignore the input reset react-select fires on blur/select.
    if (action !== 'input-change') return;
    search.current = value;
    debouncedSearch();
  };

  const onMenuScrollToBottom = () => {
    if (isLoading || !hasMore.current) return;
    fetchPage(page.current + 1);
  };

  const onMenuOpen = () => {
    if (!options.length) fetchPage(1);
  };

  return (
    <div data-cy={dataCy}>
      <Select
        options={options}
        isMulti={isMulti}
        value={selected}
        classNamePrefix="input--select"
        theme={(theme) => selectTheme(theme)}
        components={{ DropdownIndicator }}
        onChange={onSelectionChange}
        onInputChange={onInputChange}
        onMenuOpen={onMenuOpen}
        onMenuScrollToBottom={onMenuScrollToBottom}
        // Searching happens server side, so keep every returned option.
        filterOption={null}
        isLoading={isLoading}
        isClearable={isClearable}
        isDisabled={disabled}
        styles={customStyles}
      />
    </div>
  );
};

AsyncSelectBox.propTypes = {
  defaultValue: PropTypes.any,
  valueChanged: PropTypes.func,
  loadOptions: PropTypes.func.isRequired,
  isMulti: PropTypes.bool,
  isClearable: PropTypes.bool,
  disabled: PropTypes.bool,
  dataCy: PropTypes.string,
};

export default AsyncSelectBox;
